import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { type InfiniteData, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/vue-query'

import { CoursesService } from '../services/courses.service'

import { sanitizeCourseTagIds } from '../utils/courses-filtering'

import type {
  AccessibleCourseGroup,
  AddCourseToGroupInput,
  CourseSortByValue,
  CreateCourseInput,
  GroupCourseTagsResponse,
  GroupCoursesPaginatedResponse,
  ICourseProduct,
  IGroupCourseRef,
  LibraryCoursesProgressResponse,
  ReorderCoursesInput,
  SortDirectionValue,
  UpdateGroupCourseInput,
  UserCoursesPaginatedResponse,
} from '../types'

export const USER_COURSES_PAGE_SIZE = 20

export const GROUP_COURSES_PAGE_SIZE = 20

export const useCoursesQueryStore = (groupId: MaybeRefOrGetter<string | undefined>, locationId: MaybeRefOrGetter<string | undefined>) => {
  const queryClient = useQueryClient()
  const service = new CoursesService()

  // ===== QUERY KEY FACTORY =====
  // The infinite user-courses key is prefixed under `user`, so `invalidateQueries({ queryKey: userCourses(...) })`
  // matches (and refreshes) both the legacy full-list query and the paginated infinite query.
  // The infinite group-courses key is prefixed under `group` for the same reason.
  const queryKeys = {
    all: (groupId?: string, locationId?: string) => ['courses', groupId, locationId] as const,
    groupCourses: (groupId?: string, locationId?: string) => ['courses', groupId, locationId, 'group'] as const,
    groupCoursesInfinite: (
      groupId?: string,
      locationId?: string,
      limit?: number,
      tagIds?: readonly string[],
      sortBy?: CourseSortByValue,
      sort?: SortDirectionValue
    ) =>
      // Tag ids are pre-sorted so `queryKey` equality survives set toggles that produce the
      // same members in a different order (avoids spurious infinite-query resets).
      [
        'courses',
        groupId,
        locationId,
        'group',
        'infinite',
        limit,
        tagIds && tagIds.length > 0 ? [...tagIds].sort() : undefined,
        sortBy,
        sort,
      ] as const,
    userCourses: (groupId?: string, locationId?: string) => ['courses', groupId, locationId, 'user'] as const,
    userCoursesInfinite: (
      groupId?: string,
      locationId?: string,
      limit?: number,
      tagIds?: readonly string[],
      sortBy?: CourseSortByValue,
      sort?: SortDirectionValue
    ) =>
      // Tag ids are pre-sorted so `queryKey` equality survives set toggles that produce the
      // same members in a different order (avoids spurious infinite-query resets).
      [
        'courses',
        groupId,
        locationId,
        'user',
        'infinite',
        limit,
        tagIds && tagIds.length > 0 ? [...tagIds].sort() : undefined,
        sortBy,
        sort,
      ] as const,
    locationCourses: (locationId?: string) => ['courses', locationId, 'location'] as const,
    // Full unpaginated list of location-group courses that can be added to a group.
    // Admin-only pool used by the Add Course dropdown (paginated lists only surface loaded
    // pages, so they can't be trusted here).
    addableCourses: (groupId?: string, locationId?: string) => ['courses', groupId, locationId, 'addable'] as const,
    courseDetail: (courseId: string, groupId?: string, locationId?: string) =>
      ['courses', groupId, locationId, 'detail', courseId] as const,
    coursesSuggestions: (groupId?: string, locationId?: string, search?: string) =>
      ['courses', groupId, locationId, 'suggestions', search] as const,
    courseTags: (groupId?: string, locationId?: string) => ['courses', groupId, locationId, 'course-tags'] as const,
    accessibleGroupsForCourse: (locationId?: string, productId?: string) =>
      ['courses', locationId, 'accessible-groups', productId] as const,
    // Progress subtree. `progressAll` is the invalidation root — bust the whole progress
    // surface (e.g. after a lesson-completion mutation) without touching the courses lists.
    // `progressBatch` is one leaf per page of loaded courses; ids MUST be pre-sorted so two
    // batches with the same members hit the same cache entry regardless of arrival order.
    progressAll: (groupId?: string, locationId?: string) => ['courses', groupId, locationId, 'progress'] as const,
    progressBatch: (groupId?: string, locationId?: string, sortedProductIds?: readonly string[]) =>
      ['courses', groupId, locationId, 'progress', 'batch', sortedProductIds] as const,
  }

  // ===== PROGRESS ID RESOLVER =====

  /**
   * Resolve the id we send to (and receive back from) the library progress endpoint. The
   * backend expects `productId`s — `course.id` is an offer/document id in some list responses
   * and doesn't match the keys the progress map returns. Reorder + share-as-post code paths
   * use the same `productId ?? id` fallback for the same reason.
   */
  const resolveProgressId = (course: Pick<ICourseProduct, 'id' | 'productId'>): string | undefined => {
    return course.productId || course.id || undefined
  }

  // ===== QUERY CREATION FUNCTIONS =====

  const getLocationCoursesQuery = () => {
    return useQuery({
      queryKey: computed(() => {
        const locationIdValue = toValue(locationId)
        return queryKeys.locationCourses(locationIdValue)
      }),
      queryFn: async () => {
        const locationIdValue = toValue(locationId)
        if (!locationIdValue) {
          throw new Error('Location ID is required')
        }
        return service.fetchAllLocationCourses(locationIdValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId)),
    })
  }

  const createUserCoursesQuery = () => {
    return useQuery({
      queryKey: computed(() => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        return queryKeys.userCourses(groupIdValue, locationIdValue)
      }),
      queryFn: async () => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.fetchUserCourses(groupIdValue, locationIdValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(groupId) && !!toValue(locationId)),
    })
  }

  /**
   * Paginated user courses (v2 API contract): fetches one page at a time via `useInfiniteQuery`.
   * Consumers should read `allData` for the flattened list and call `fetchNextPage()` to load the next page.
   *
   * Filter/sort params are reactive: when `tagIds`, `sortBy`, or `sort` change the queryKey changes
   * and TanStack Query starts a fresh fetch from page 1 automatically — no manual "reset pageNo"
   * bookkeeping needed on the caller side.
   *
   * `tagIds` must be 24-char hex ObjectIds — the backend rejects anything else with a 400. We
   * sanitize aggressively before the request so a stale/legacy id in state never poisons the fetch.
   */
  const fetchUserCoursesInfiniteQuery = (options?: {
    limit?: MaybeRefOrGetter<number>
    tagIds?: MaybeRefOrGetter<readonly string[] | undefined>
    sortBy?: MaybeRefOrGetter<CourseSortByValue | undefined>
    sort?: MaybeRefOrGetter<SortDirectionValue | undefined>
    /**
     * Extra caller-side gate ANDed with the internal `groupId + locationId` check. Consumers
     * (e.g. `LearningHome`) pass `authStore.isLoggedIn` here so the authenticated endpoint
     * never fires for guests — filter/sort changes must not trigger a `user/courses` fetch
     * when the caller isn't authenticated.
     */
    enabled?: MaybeRefOrGetter<boolean>
  }) => {
    const limit = options?.limit ?? USER_COURSES_PAGE_SIZE
    const infiniteQuery = useInfiniteQuery<
      UserCoursesPaginatedResponse,
      Error,
      InfiniteData<UserCoursesPaginatedResponse, number>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        const limitValue = toValue(limit)
        const tagIdsValue = sanitizeCourseTagIds(toValue(options?.tagIds))
        const sortByValue = toValue(options?.sortBy)
        const sortValue = toValue(options?.sort)
        return queryKeys.userCoursesInfinite(groupIdValue, locationIdValue, limitValue, tagIdsValue, sortByValue, sortValue)
      }),
      queryFn: async ({ pageParam }) => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        const limitValue = toValue(limit)
        const tagIdsValue = sanitizeCourseTagIds(toValue(options?.tagIds))
        const sortByValue = toValue(options?.sortBy)
        const sortValue = toValue(options?.sort)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.fetchUserCoursesPaginated(groupIdValue, locationIdValue, {
          pageNo: pageParam,
          limit: limitValue,
          tagIds: tagIdsValue.length > 0 ? tagIdsValue : undefined,
          sortBy: sortByValue,
          sort: sortValue,
        })
      },
      initialPageParam: 1,
      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.totalPages <= 0) return undefined
        if (lastPage.pageNo >= lastPage.totalPages) return undefined
        return lastPage.pageNo + 1
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => {
        if (!toValue(groupId) || !toValue(locationId)) return false
        // Default to `true` when the caller omits `enabled` so existing consumers (tests, admin
        // dropdowns) keep firing; only opt-out when the caller explicitly passes `false`.
        const callerEnabled = toValue(options?.enabled)
        return callerEnabled ?? true
      }),
    })

    // One batch of product ids per fetched page. Ids are deduped and sorted so two batches
    // with the same members hit the same cache entry (queryKey identity is stable across
    // set toggles that produce members in a different order).
    const productIdBatches = computed<string[][]>(() => {
      const pages = infiniteQuery.data?.value?.pages
      if (!pages) return []
      return pages.map(page => {
        const ids = new Set<string>()
        for (const course of page.courses) {
          const pid = resolveProgressId(course)
          if (pid) ids.add(pid)
        }
        return Array.from(ids).sort()
      })
    })

    // Progress observers, one per page's batch. Each observer is keyed under
    // `queryKeys.progressBatch(...)` so:
    //   - existing batches keep their observers (no refetch) when a new page lands
    //   - two mounts of the same feature share the batch cache entry
    //   - filter/sort changes tear down all courses observers *and* progress observers
    //     naturally because the underlying queryKeys change together
    // `combine` folds every batch's response into a single `{ progressMap, isFetching }`
    // that `allData` reads on-render. `isFetching` bubbles up so consumers can show a
    // subtle "progress loading" affordance without waiting for all batches to resolve.
    //
    // Auth prerequisite: mirror the parent list's `options.enabled` gate on every batch
    // observer. Without this, cached pages from a prior logged-in session would keep
    // producing `productIdBatches` after logout, and the progress endpoint (which is
    // authenticated + user-scoped) could still be hit from a logged-out view.
    const isProgressEnabled = computed(() => toValue(options?.enabled) ?? true)
    const progressQueries = useQueries({
      queries: computed(() => {
        const gid = toValue(groupId)
        const lid = toValue(locationId)
        if (!gid || !lid) return []
        return productIdBatches.value
          .filter(ids => ids.length > 0)
          .map(ids => ({
            queryKey: queryKeys.progressBatch(gid, lid, ids),
            queryFn: () => service.fetchLibraryCoursesProgress(gid, lid, ids),
            staleTime: 5 * 60 * 1000,
            enabled: isProgressEnabled,
          }))
      }),
      combine: results => {
        const progressMap: Record<string, number> = {}
        let isFetching = false
        for (const result of results) {
          if (result.isFetching) isFetching = true
          const data = result.data as LibraryCoursesProgressResponse | undefined
          if (!data) continue
          for (const [pid, entry] of Object.entries(data)) {
            progressMap[pid] = entry.progress
          }
        }
        return { progressMap, isFetching }
      },
    })

    // Flattened list of all loaded courses across pages, with progress merged on-read
    // from the combined progressQueries. Progress lives inside TanStack cache via the
    // per-batch observers, so refetches / invalidations propagate through the observer
    // graph and Vue re-runs `allData` automatically.
    const allData = computed<ICourseProduct[]>(() => {
      const pages = infiniteQuery.data?.value?.pages
      if (!pages) return []
      const progress = progressQueries.value.progressMap
      return pages.flatMap(page =>
        page.courses.map(course => {
          const pid = resolveProgressId(course)
          if (!pid) return course
          const p = progress[pid]
          if (p === undefined) return course
          return { ...course, progress: p }
        })
      )
    })

    // Server-reported total (from the first page); useful for UI counters and empty-state gating.
    const total = computed<number>(() => infiniteQuery.data?.value?.pages?.[0]?.total ?? 0)

    return {
      ...infiniteQuery,
      allData,
      total,
      // Exposed for consumers that want a subtle "progress loading" indicator; the courses
      // list itself renders immediately regardless of this.
      isProgressFetching: computed(() => progressQueries.value.isFetching),
    }
  }

  /**
   * Fetch the deduped catalog of tags in use by any course in the group.
   * Drives the filter panel — group the returned `tags` by `category` client-side.
   *
   * Backend caches for 3h and auto-invalidates on course add/update/remove in the group, so
   * the client only needs to fetch once per page load; a 5 minute staleTime keeps most
   * mounts from re-firing this while still refetching if the user navigates away and back.
   */
  const fetchCourseTagsQuery = () => {
    return useQuery<GroupCourseTagsResponse>({
      queryKey: computed(() => queryKeys.courseTags(toValue(groupId), toValue(locationId))),
      queryFn: async () => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.fetchCourseTags(groupIdValue, locationIdValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(groupId) && !!toValue(locationId)),
    })
  }

  const createGroupCoursesQuery = () => {
    return useQuery({
      queryKey: computed(() => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        return queryKeys.groupCourses(groupIdValue, locationIdValue)
      }),
      queryFn: async () => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.fetchGroupCourses(groupIdValue, locationIdValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(groupId) && !!toValue(locationId)),
    })
  }

  /**
   * Paginated group courses (v2 API contract, admin/portal group view): fetches one page at a time via `useInfiniteQuery`.
   * Consumers should read `allData` for the flattened list and call `fetchNextPage()` to load the next page.
   * Unlike the user-courses infinite query, `total` here matches the returned page counts (no access post-filter).
   *
   * Filter/sort params share the same contract as `fetchUserCoursesInfiniteQuery` — logged-out
   * viewers (which fall back to this query) get the same filtering + sorting behavior as learners.
   * When `tagIds`, `sortBy`, or `sort` change the queryKey changes and TanStack Query starts a
   * fresh fetch from page 1 automatically.
   */
  const fetchGroupCoursesInfiniteQuery = (options?: {
    limit?: MaybeRefOrGetter<number>
    tagIds?: MaybeRefOrGetter<readonly string[] | undefined>
    sortBy?: MaybeRefOrGetter<CourseSortByValue | undefined>
    sort?: MaybeRefOrGetter<SortDirectionValue | undefined>
    /**
     * Extra caller-side gate ANDed with the internal `groupId + locationId` check. `LearningHome`
     * passes `!authStore.isLoggedIn` here so guests hit only this endpoint while learners hit the
     * user endpoint — filter/sort changes never fire both APIs from the same view.
     */
    enabled?: MaybeRefOrGetter<boolean>
  }) => {
    const limit = options?.limit ?? GROUP_COURSES_PAGE_SIZE
    const infiniteQuery = useInfiniteQuery<
      GroupCoursesPaginatedResponse,
      Error,
      InfiniteData<GroupCoursesPaginatedResponse, number>,
      readonly unknown[],
      number
    >({
      queryKey: computed(() => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        const limitValue = toValue(limit)
        const tagIdsValue = sanitizeCourseTagIds(toValue(options?.tagIds))
        const sortByValue = toValue(options?.sortBy)
        const sortValue = toValue(options?.sort)
        return queryKeys.groupCoursesInfinite(groupIdValue, locationIdValue, limitValue, tagIdsValue, sortByValue, sortValue)
      }),
      queryFn: async ({ pageParam }) => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        const limitValue = toValue(limit)
        const tagIdsValue = sanitizeCourseTagIds(toValue(options?.tagIds))
        const sortByValue = toValue(options?.sortBy)
        const sortValue = toValue(options?.sort)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.fetchGroupCoursesPaginated(groupIdValue, locationIdValue, {
          pageNo: pageParam,
          limit: limitValue,
          tagIds: tagIdsValue.length > 0 ? tagIdsValue : undefined,
          sortBy: sortByValue,
          sort: sortValue,
        })
      },
      initialPageParam: 1,
      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.totalPages <= 0) return undefined
        if (lastPage.pageNo >= lastPage.totalPages) return undefined
        return lastPage.pageNo + 1
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => {
        if (!toValue(groupId) || !toValue(locationId)) return false
        // Default to `true` when the caller omits `enabled` so existing consumers (admin/portal
        // group view, share-as-post) keep firing; only opt-out when the caller explicitly passes
        // `false` (e.g. `LearningHome` gating this to guests only).
        const callerEnabled = toValue(options?.enabled)
        return callerEnabled ?? true
      }),
    })

    // Flattened list of all loaded courses across pages.
    const allData = computed<ICourseProduct[]>(() => {
      const pages = infiniteQuery.data?.value?.pages
      if (!pages) return []
      return pages.flatMap(page => page.courses)
    })

    // Server-reported total (from the first page); useful for UI counters and empty-state gating.
    const total = computed<number>(() => infiniteQuery.data?.value?.pages?.[0]?.total ?? 0)

    return {
      ...infiniteQuery,
      allData,
      total,
    }
  }

  const createCourseDetailQuery = (courseId: MaybeRefOrGetter<string>) => {
    return useQuery({
      queryKey: computed(() => {
        const courseIdValue = toValue(courseId)
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        return queryKeys.courseDetail(courseIdValue, groupIdValue, locationIdValue)
      }),
      queryFn: async () => {
        const courseIdValue = toValue(courseId)
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        if (!courseIdValue || !groupIdValue || !locationIdValue) {
          throw new Error('Course ID, Group ID and Location ID are required')
        }
        return service.getCourseById(courseIdValue, groupIdValue, locationIdValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(courseId) && !!toValue(groupId) && !!toValue(locationId)),
    })
  }

  const createCoursesSuggestionsQuery = (searchQuery: MaybeRefOrGetter<string>) => {
    return useQuery({
      queryKey: computed(() => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        const searchValue = toValue(searchQuery)
        return queryKeys.coursesSuggestions(groupIdValue, locationIdValue, searchValue)
      }),
      queryFn: async () => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        const searchValue = toValue(searchQuery)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.getCoursesSuggestions(groupIdValue, locationIdValue, searchValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(groupId) && !!toValue(locationId)),
    })
  }

  /**
   * Fetch all location-based courses (courses available in the location that can be added to groups)
   */
  const createLocationCoursesQuery = () => {
    return useQuery({
      queryKey: computed(() => {
        const locationIdValue = toValue(locationId)
        return queryKeys.locationCourses(locationIdValue)
      }),
      queryFn: async () => {
        const locationIdValue = toValue(locationId)
        if (!locationIdValue) {
          throw new Error('Location ID is required')
        }
        return service.fetchAllLocationCourses(locationIdValue)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId)),
    })
  }

  /**
   * Fetch the pool of location-group courses that can be added to the current group as a
   * lightweight reference list. Admin-only — callers gate via `options.enabled`
   * (e.g. `() => props.isGroupAdmin`).
   *
   * Backed by `/{locationId}/groups/{groupId}/candidate-courses`. The backend already
   * excludes courses that are attached to the group, so the response is the ready-to-add
   * pool for the Add Course dropdown. The paginated user/group courses queries only
   * surface loaded pages and therefore can't drive this UI.
   */
  const fetchAddableCoursesQuery = (options?: { enabled?: MaybeRefOrGetter<boolean> }) => {
    return useQuery({
      queryKey: computed(() => queryKeys.addableCourses(toValue(groupId), toValue(locationId))),
      queryFn: async () => {
        const groupIdValue = toValue(groupId)
        const locationIdValue = toValue(locationId)
        if (!groupIdValue || !locationIdValue) {
          throw new Error('Group ID and Location ID are required')
        }
        return service.fetchAddableCourses(groupIdValue, locationIdValue)
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => {
        if (!toValue(groupId) || !toValue(locationId)) return false
        const callerEnabled = toValue(options?.enabled)
        // Default false — this endpoint is admin-only and callers must opt-in explicitly.
        return callerEnabled ?? false
      }),
    })
  }

  const fetchAccessibleGroupsForCourse = async (productId: string): Promise<AccessibleCourseGroup[]> => {
    const locationIdValue = toValue(locationId)
    if (!locationIdValue) {
      throw new Error('Location ID is required')
    }
    if (!productId) {
      throw new Error('Product ID is required')
    }

    return await queryClient.fetchQuery({
      queryKey: queryKeys.accessibleGroupsForCourse(locationIdValue, productId),
      queryFn: () => service.fetchAccessibleGroupsForCourse(locationIdValue, productId),
      staleTime: 5 * 60 * 1000,
    })
  }

  // ===== MUTATIONS =====

  const createCourseMutation = useMutation({
    mutationFn: async (data: CreateCourseInput) => {
      const groupIdValue = toValue(groupId)
      const locationIdValue = toValue(locationId)
      if (!groupIdValue || !locationIdValue) {
        throw new Error('Group ID and Location ID are required')
      }
      return service.createCourse(data, groupIdValue, locationIdValue)
    },
    onSuccess: newCourse => {
      // Invalidate every course-scoped query for this group so the new course, its tags,
      // and any suggestions surface everywhere — not just the legacy `groupCourses` list.
      // (`queryKeys.all` is prefix-scoped to `['courses', groupId, locationId]`, so posts,
      // members, feed, etc. are untouched.)
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(toValue(groupId), toValue(locationId)),
      })
      // Add optimistic course to cache
      queryClient.setQueryData(queryKeys.groupCourses(toValue(groupId), toValue(locationId)), (old: ICourseProduct[] | undefined) => {
        if (!old) return [newCourse]
        return [newCourse, ...old]
      })
    },
  })

  const updateCourseMutation = useMutation({
    mutationFn: async ({ courseId, data }: { courseId: string; data: UpdateGroupCourseInput }) => {
      const groupIdValue = toValue(groupId)
      const locationIdValue = toValue(locationId)
      if (!groupIdValue || !locationIdValue) {
        throw new Error('Group ID and Location ID are required')
      }
      return service.updateCourse(courseId, data, groupIdValue, locationIdValue)
    },
    onSuccess: updatedCourse => {
      // Invalidate all course queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(toValue(groupId), toValue(locationId)),
      })
      // Update course in cache
      queryClient.setQueryData(queryKeys.courseDetail(updatedCourse.id || '', toValue(groupId), toValue(locationId)), updatedCourse)
    },
  })

  const deleteCourseMutation = useMutation({
    mutationFn: async (courseId: string) => {
      const groupIdValue = toValue(groupId)
      const locationIdValue = toValue(locationId)
      if (!groupIdValue || !locationIdValue) {
        throw new Error('Group ID and Location ID are required')
      }
      return service.deleteCourse(courseId, groupIdValue, locationIdValue)
    },
    onSuccess: (_, courseId) => {
      const gid = toValue(groupId)
      const lid = toValue(locationId)
      // Invalidate all course queries (prefix match also covers `addableCourses`, so the
      // Add Course dropdown re-offers the deleted course after refetch).
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(gid, lid),
      })
      // Remove course from cache
      queryClient.setQueryData(queryKeys.groupCourses(gid, lid), (old: ICourseProduct[] | undefined) => {
        if (!old) return []
        return old.filter(course => course.id !== courseId)
      })
    },
  })

  const reorderCoursesMutation = useMutation({
    mutationFn: async (data: ReorderCoursesInput) => {
      return service.reorderCourses(data)
    },
    onSuccess: () => {
      // Invalidate group + user courses queries to refetch with new order
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupCourses(toValue(groupId), toValue(locationId)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.userCourses(toValue(groupId), toValue(locationId)),
      })
    },
  })

  const addCourseToGroupMutation = useMutation({
    mutationFn: async (input: AddCourseToGroupInput) => {
      const groupIdValue = toValue(groupId)
      const locationIdValue = toValue(locationId)
      if (!groupIdValue || !locationIdValue) {
        throw new Error('Group ID and Location ID are required')
      }
      return service.addCourseToGroup(groupIdValue, locationIdValue, input)
    },
    onSuccess: newCourse => {
      const gid = toValue(groupId)
      const lid = toValue(locationId)
      // Invalidate every course-scoped query for this group so the newly added course, its
      // tags, and any suggestions surface everywhere. Same rationale as `createCourseMutation`.
      // (Prefix match also invalidates the `addableCourses` key so the Add Course dropdown
      // drops the newly attached course on refetch.)
      queryClient.invalidateQueries({
        queryKey: queryKeys.all(gid, lid),
      })
      // Add optimistic course to cache
      queryClient.setQueryData(queryKeys.groupCourses(gid, lid), (old: ICourseProduct[] | undefined) => {
        if (!old) return [newCourse]
        return [...old, newCourse]
      })
      // Optimistically remove the newly attached course from the addable pool so the Add
      // Course dropdown hides it before the invalidation-driven refetch resolves. Addable
      // entries only carry `productId` (they aren't attached yet, so no `offerId`).
      queryClient.setQueryData(queryKeys.addableCourses(gid, lid), (old: IGroupCourseRef[] | undefined) => {
        if (!old) return old
        const productId = newCourse.productId
        if (!productId) return old
        return old.filter(course => course.productId !== productId)
      })
    },
  })

  const purchaseCourseMutation = useMutation({
    mutationFn: async ({ offerId }: { offerId: string }) => {
      const groupIdValue = toValue(groupId)
      const locationIdValue = toValue(locationId)
      if (!groupIdValue || !locationIdValue) {
        throw new Error('Group ID and Location ID are required')
      }
      return service.purchaseCourse(groupIdValue, locationIdValue, offerId)
    },
    onSuccess: () => {
      // Course access changed for this user — refresh user courses and any course detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.userCourses(toValue(groupId), toValue(locationId)),
      })
      queryClient.invalidateQueries({ queryKey: ['library'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['learner-api'] })
    },
  })

  return {
    // Query creation functions
    getLocationCoursesQuery,
    createUserCoursesQuery,
    fetchUserCoursesInfiniteQuery,
    createGroupCoursesQuery,
    fetchGroupCoursesInfiniteQuery,
    fetchCourseTagsQuery,
    createCourseDetailQuery,
    createCoursesSuggestionsQuery,
    createLocationCoursesQuery,
    fetchAddableCoursesQuery,
    fetchAccessibleGroupsForCourse,

    // Mutations
    createCourseMutation,
    updateCourseMutation,
    deleteCourseMutation,
    reorderCoursesMutation,
    addCourseToGroupMutation,
    purchaseCourseMutation,

    // Query keys for manual invalidation
    queryKeys,
  }
}
